import {
	Box,
	Button,
	Col,
	Component,
	Divider,
	Grid,
	Heading,
	Icons,
	Inline,
	Select,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import {
	Form,
	GetFieldValue,
	MarkdownInputField,
	RadioInputField,
	SelectField,
	SelectInputField,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	isTruthy,
	useFieldValue,
	useForm,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { ReorderSections } from "./ReorderSections"
import {
	BORDER_WIDTH_OPTIONS,
	TBrandWiseColors,
	TFontFamily,
	TTripQuoteEmailTemplate,
} from "./store"
import { useCallback, useMemo } from "react"
import {
	GreetingsTemplateField,
	JustifiedContentToggleField,
	SelectFontFamilyField,
	SubjectLineTemplateField,
} from "./SharedFormFields"

export type TEmailPdfTemplateCreatePayload = Omit<
	TTripQuoteEmailTemplate,
	"id"
> & {
	variables: Array<{
		id: string
		name: string
		description: string
		placeholder: string
	}>
	subject_line_variables: Array<{
		id: string
		name: string
		description: string
		placeholder: string
	}>
	font_families: Array<TFontFamily>
	border_style_options?: Array<string>
	section_title_settings_options?: {
		background: Array<string>
		border: Array<string>
		rounded: Array<string>
		textAlign: Array<string>
	}
	transport_and_activity_section_settings: {
		variables: Array<string>
		default_variant: string
		variants: Array<{
			id: string
			name: string
			description: string
		}>
		show_activity_start_time: 0 | 1 | "0" | "1"
	}
	hotel_section_settings: {
		similar_word_in_hotel: 0 | 1 | "0" | "1"
		show_package_price_below_hotel: 0 | 1 | "0" | "1"
	}
}

export function EditTripQuoteEmailTemplateForm({
	onCancel,
	onSubmit,
	initialValues,
	tenantColor,
}: {
	onCancel: () => void
	onSubmit: (
		data: Pick<
			TEmailPdfTemplateCreatePayload,
			"name" | "greetings" | "subject_line" | "section_ordering_settings"
		> &
			Pick<
				TTripQuoteEmailTemplate,
				"transport_and_activity_section_settings"
			> & {
				settings: Omit<
					TEmailPdfTemplateCreatePayload["settings"],
					"fontFamily"
				> & {
					fontFamily: string
				}
			}
	) => Promise<unknown>
	initialValues: TEmailPdfTemplateCreatePayload
	tenantColor: TBrandWiseColors
}) {
	const {
		name,
		greetings,
		subject_line,
		settings,
		variables,
		subject_line_variables,
		font_families,
		transport_and_activity_section_settings,
		daywise_schedule_section_settings,
		other_info_section_settings,
		terms_section_settings,
		border_style_options,
		section_title_settings,
		section_title_settings_options,
		section_ordering_settings,
		hotel_section_settings,
		visa_section_settings,
	} = initialValues
	return (
		<Form<{
			name: string
			settings: TEmailPdfTemplateCreatePayload["settings"]
			section_title_settings: TEmailPdfTemplateCreatePayload["section_title_settings"]
			daywise_schedule_section_settings?: TEmailPdfTemplateCreatePayload["daywise_schedule_section_settings"]
			other_info_section_settings?: TEmailPdfTemplateCreatePayload["other_info_section_settings"]
			terms_section_settings?: TEmailPdfTemplateCreatePayload["terms_section_settings"]
			section_ordering_settings?: TEmailPdfTemplateCreatePayload["section_ordering_settings"]
			greetings: string
			subject_line: string
			transport_and_activity_section_settings: Omit<
				TEmailPdfTemplateCreatePayload["transport_and_activity_section_settings"],
				"variant"
			> & {
				variant?: TEmailPdfTemplateCreatePayload["transport_and_activity_section_settings"]["variants"][number]
			}
			hotel_section_settings: TEmailPdfTemplateCreatePayload["hotel_section_settings"]
			visa_section_settings: TEmailPdfTemplateCreatePayload["visa_section_settings"]
		}>
			initialValues={{
				name,
				settings,
				greetings,
				subject_line: subject_line || "",
				transport_and_activity_section_settings: {
					...transport_and_activity_section_settings,
					variant: transport_and_activity_section_settings.variants.find(
						(v) => transport_and_activity_section_settings.variant === v.id
					),
				},
				other_info_section_settings,
				terms_section_settings,
				section_title_settings,
				section_ordering_settings,
				daywise_schedule_section_settings: daywise_schedule_section_settings
					? {
							...daywise_schedule_section_settings,
							// transform for our radio items
							hide_travel_distance: isTruthy(
								daywise_schedule_section_settings.hide_travel_distance
							)
								? "1"
								: "0",
							show_same_day_service_together: isTruthy(
								daywise_schedule_section_settings.show_same_day_service_together
							)
								? "1"
								: "0",
						}
					: undefined,
				hotel_section_settings: hotel_section_settings
					? {
							...hotel_section_settings,
							// transform for our radio items
							similar_word_in_hotel: isTruthy(
								hotel_section_settings.similar_word_in_hotel
							)
								? "1"
								: "0",
							show_package_price_below_hotel: isTruthy(
								hotel_section_settings.show_package_price_below_hotel
							)
								? "1"
								: "0",
						}
					: undefined,
				visa_section_settings,
			}}
			onSubmit={withServerErrors(
				async ({
					settings: {
						justifiedText,
						fontFamily,
						borderStyle,
						...otherSettings
					},
					transport_and_activity_section_settings,
					...values
				}) => {
					const { greetings } = values
					const contentWithPlaceholders = [
						greetings,
						otherSettings.watermarkText || "",
					].filter((s) => Boolean(s))
					const invalidPlaceholders: Array<string> = []
					contentWithPlaceholders.forEach((content) => {
						if (!content) return
						// check for non-existing variable placeholders
						const placeholderRegex = /\{\{\s*([\w\d]+)\s*\}\}/gi
						let result
						while ((result = placeholderRegex.exec(content)) !== null) {
							const p = result[1]
							if (
								!variables.find((v) => v.placeholder === "{{" + p.trim() + "}}")
							) {
								invalidPlaceholders.push(p)
							}
						}
					})
					if (invalidPlaceholders.length) {
						throw new Error(
							`Invalid placeholder variables: ${invalidPlaceholders.join(
								", "
							)}. Only available variables are allowed.`
						)
					}
					// The subject line has its own allowed token set.
					const invalidSubjectPlaceholders: Array<string> = []
					if (values.subject_line) {
						const placeholderRegex = /\{\{\s*([\w\d]+)\s*\}\}/gi
						let result
						while (
							(result = placeholderRegex.exec(values.subject_line)) !== null
						) {
							const p = result[1]
							if (
								!subject_line_variables.find(
									(v) => v.placeholder === "{{" + p.trim() + "}}"
								)
							) {
								invalidSubjectPlaceholders.push(p)
							}
						}
					}
					if (invalidSubjectPlaceholders.length) {
						throw new Error(
							`Invalid subject placeholder variables: ${invalidSubjectPlaceholders.join(
								", "
							)}. Only available variables are allowed.`
						)
					}
					borderStyle = borderStyle ? borderStyle.toLowerCase() : undefined
					await onSubmit({
						...values,
						// A cleared text field becomes undefined and would be dropped
						// from the JSON body, leaving the old subject in place — send
						// an explicit null so the backend clears it.
						subject_line: values.subject_line || null,
						settings: {
							...otherSettings,
							borderTopStyle: borderStyle,
							borderRightStyle: borderStyle,
							borderBottomStyle: borderStyle,
							borderLeftStyle: borderStyle,
							fontFamily: fontFamily.id,
							justifiedText: isTruthy(justifiedText) ? 1 : 0,
						},
						transport_and_activity_section_settings: {
							title: transport_and_activity_section_settings.title,
							show_activity_start_time:
								transport_and_activity_section_settings.show_activity_start_time,
							variant:
								transport_and_activity_section_settings.variant?.id ||
								transport_and_activity_section_settings.variants[0].id,
							hide_transport_summary_for_same_cabs:
								transport_and_activity_section_settings.hide_transport_summary_for_same_cabs,
							terms: transport_and_activity_section_settings.terms,
						},
						section_ordering_settings: values.section_ordering_settings
							? values.section_ordering_settings
							: section_ordering_settings,
					})
					showSnackbar("Template details saved")
				}
			)}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit, form }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">Settings</Heading>
									<Text color="muted">
										Configure template settings like fonts and text alignment.
									</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<SelectFontFamilyField
										label="Font Family"
										name="settings.fontFamily"
										fontFamilies={font_families}
										help={
											"Fonts are device dependent and might not be available on your/client's device."
										}
									/>
									<JustifiedContentToggleField />
									{border_style_options?.length ? (
										<Inline flexWrap="wrap" gap="4">
											<GetBorderWidthFieldValue settingsName="settings">
												{({ borderWidth, hasBorder, onChange }) => (
													<SelectField
														select={Select}
														name="settings.borderStyle"
														label="Border Style"
														placeholder="e.g. None"
														options={border_style_options}
														onChange={(value, name) => {
															form.change(name as never, value as never)
															if (!value || value === "none") {
																onChange(0)
															} else {
																if (!hasBorder) {
																	onChange(4)
																}
															}
														}}
														optionRenderer={({
															option,
														}: {
															option: string
														}) => {
															const bw = hasBorder
																? Math.max(...borderWidth)
																: BORDER_WIDTH_OPTIONS[1]
															return (
																<Stack>
																	<Text>{option}</Text>
																	{option.toLowerCase() === "none" ? (
																		<Text color="muted" fontSize="sm">
																			No-Border
																		</Text>
																	) : (
																		<Box
																			bgColor={"success"}
																			borderColor="primary_emphasis"
																			style={{
																				width: "100px",
																				height: `${bw * 4 < 10 ? 10 : bw * 4}px`,
																				borderWidth: hasBorder
																					? borderWidth.join("px ") + "px"
																					: `${bw}px`,
																				borderStyle: option,
																			}}
																		></Box>
																	)}
																</Stack>
															)
														}}
													/>
												)}
											</GetBorderWidthFieldValue>
											<GetFieldValue<
												string | undefined
											> name="settings.borderStyle">
												{({ value: borderStyle }) =>
													!borderStyle || borderStyle === "none" ? null : (
														<Inline gap="4">
															{["Top", "Right", "Bottom", "Left"].map(
																(side) => {
																	const fieldName = `settings.border${side}Width`
																	return (
																		<SelectInputField
																			name={fieldName}
																			max={10}
																			min={0}
																			step={1}
																			type="number"
																			label={side + " Width"}
																			placeholder="e.g. 0"
																			onChange={(e) => {
																				const value = e.currentTarget.value
																				form.change(
																					e.currentTarget.name as never,
																					value as never
																				)
																				if (Number(value || "0")) {
																					if (
																						!borderStyle ||
																						borderStyle.toLowerCase() === "none"
																					) {
																						form.change(
																							"settings.borderStyle" as never,
																							border_style_options[1] as never
																						)
																					}
																				}
																			}}
																		>
																			{BORDER_WIDTH_OPTIONS.map((width) => (
																				<option value={width} key={width}>
																					{width}
																				</option>
																			))}
																		</SelectInputField>
																	)
																}
															)}
														</Inline>
													)
												}
											</GetFieldValue>
										</Inline>
									) : null}
								</Stack>
							</Col>
						</Grid>
						{section_title_settings_options ? (
							<>
								<Divider sm />
								<Grid gap="6">
									<Col sm={12} md={4}>
										<Stack gap="1">
											<Heading fontSize="md">Section Title Settings</Heading>
											<Text color="muted">
												Customize the appearance of section's (e.g.
												Accommodation, Transportation etc.) title
											</Text>
										</Stack>
									</Col>
									<Col>
										<Stack gap="4">
											<Inline gap="4" flexWrap="wrap">
												<SelectInputField
													name="section_title_settings.background"
													label="Background Color"
												>
													{section_title_settings_options.background.map(
														(s) => (
															<option value={s} key={s}>
																{s}
															</option>
														)
													)}
												</SelectInputField>
												<SelectInputField
													name="section_title_settings.border"
													label="Border Position"
												>
													{section_title_settings_options.border.map((s) => (
														<option value={s} key={s}>
															{s}
														</option>
													))}
												</SelectInputField>
												<SelectInputField
													name="section_title_settings.textAlign"
													label="Text Align"
												>
													{section_title_settings_options.textAlign.map((s) => (
														<option value={s} key={s}>
															{s}
														</option>
													))}
												</SelectInputField>
											</Inline>
											<GetFieldValue<
												TEmailPdfTemplateCreatePayload["section_title_settings"]
											> name="section_title_settings">
												{({ value: section_title_settings }) => {
													if (!section_title_settings) return null
													const dark_fill =
														tenantColor.brand_wise_color_shades[0]
															.color_shades?.["shade-700"]
													const borderColor =
														tenantColor.brand_wise_color_shades[0]
															.color_shades?.["shade-600"] || "emphasis"
													const light_fill =
														tenantColor.brand_wise_color_shades[0]
															.color_shades?.["shade-200"]
													const textColor =
														tenantColor.brand_wise_color_shades[0]
															.color_shades?.["shade-800"]
													return (
														<Stack gap="1">
															<Text fontSize="sm" color="muted">
																Section Title Preview
															</Text>
															<Box
																paddingX="4"
																paddingY="2"
																fontWeight="semibold"
																style={{
																	borderColor: borderColor,
																	color:
																		section_title_settings.background ===
																		"dark_fill"
																			? "#fff"
																			: textColor,
																	backgroundColor:
																		section_title_settings.background === "fill"
																			? light_fill
																			: section_title_settings.background ===
																				  "dark_fill"
																				? dark_fill
																				: "#fff",
																}}
																borderLeftWidth={
																	section_title_settings.border === "left" ||
																	section_title_settings.border === "all" ||
																	section_title_settings.border === "vertical"
																		? "4"
																		: "0"
																}
																borderRightWidth={
																	section_title_settings.border === "right" ||
																	section_title_settings.border === "all" ||
																	section_title_settings.border === "vertical"
																		? "4"
																		: "0"
																}
																borderTopWidth={
																	section_title_settings.border === "top" ||
																	section_title_settings.border === "all" ||
																	section_title_settings.border === "horizontal"
																		? "4"
																		: "0"
																}
																borderBottomWidth={
																	section_title_settings.border === "bottom" ||
																	section_title_settings.border === "all" ||
																	section_title_settings.border === "horizontal"
																		? "4"
																		: "0"
																}
																textAlign={
																	section_title_settings.textAlign !== "auto"
																		? section_title_settings.textAlign
																		: undefined
																}
															>
																Sample Title Text
															</Box>
														</Stack>
													)
												}}
											</GetFieldValue>
										</Stack>
									</Col>
								</Grid>
							</>
						) : null}
						<Divider sm />
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">Settings for Hotel Section</Heading>
									<Text color="muted">
										Customize the settings of Hotel section
									</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<TextInputField
										label="Hotel Section Title"
										name="hotel_section_settings.title"
										placeholder="e.g. Hotel & Accommodation"
									/>
									<Stack gap="2">
										<Text fontWeight="semibold">Package Price Location</Text>
										<Inline gap="4" collapseBelow="md">
											<Box flex="1" padding="2" rounded="md" borderWidth="1">
												<RadioInputField
													name="hotel_section_settings.show_package_price_below_hotel"
													label="Show package price separately"
													value="0"
													expandLabel
													help={
														<Stack gap="2" color="default">
															<Divider marginY="0" />
															<Box fontSize="xs">
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	backgroundColor="inset"
																	paddingY="1"
																	paddingX="2"
																	marginBottom="1"
																>
																	Hotels
																</Box>
																<Stack paddingLeft="2">
																	<Text>Option 1:</Text>
																	<Stack paddingLeft="3">
																		<Text as="li">
																			Hotel A (1st, 2nd Nights)
																		</Text>
																		<Text as="li">
																			Hotel B (3rd, 4th Nights)
																		</Text>
																	</Stack>
																</Stack>
																<Divider marginY="1" />
																<Stack paddingLeft="2">
																	<Text>Option 2:</Text>
																	<Stack paddingLeft="3">
																		<Text as="li">
																			Hotel C (1st, 2nd Nights)
																		</Text>
																		<Text as="li">
																			Hotel D (3rd, 4th Nights)
																		</Text>
																	</Stack>
																</Stack>
															</Box>
															<Box
																fontWeight="semibold"
																width="full"
																fontSize="sm"
																backgroundColor="inset"
																paddingY="1"
																paddingX="2"
															>
																Total Price
															</Box>
															<Inline paddingLeft="2">
																<Box flex="1">
																	<Text fontWeight="semibold" fontSize="xs">
																		Option 1: <br />
																		xxx
																	</Text>
																</Box>
																<Box flex="1">
																	<Text fontWeight="semibold" fontSize="xs">
																		Option 2: <br />
																		xxx
																	</Text>
																</Box>
															</Inline>
														</Stack>
													}
												/>
											</Box>
											<Box flex="1" padding="2" rounded="md" borderWidth="1">
												<RadioInputField
													name="hotel_section_settings.show_package_price_below_hotel"
													label="Show package price with hotels"
													value="1"
													expandLabel
													help={
														<Stack gap="2" color="default">
															<Divider marginY="0" />
															<Box fontSize="xs">
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	backgroundColor="inset"
																	paddingY="1"
																	paddingX="2"
																	marginBottom="1"
																>
																	Hotels
																</Box>
																<Stack paddingLeft="2" marginBottom="1">
																	<Text>Option 1:</Text>
																	<Stack paddingLeft="4">
																		<Text as="li">
																			Hotel A (1st, 2nd Nights)
																		</Text>
																		<Text as="li">
																			Hotel B (3rd, 4th Nights)
																		</Text>
																	</Stack>
																</Stack>
																<Box fontWeight="semibold" paddingLeft="2">
																	Total Price
																	<br />
																	xxx
																</Box>
															</Box>
															<Divider marginY="0" />
															<Box fontSize="xs">
																<Stack paddingLeft="2" marginBottom="1">
																	<Text>Option 2:</Text>
																	<Stack paddingLeft="4">
																		<Text as="li">
																			Hotel C (1st, 2nd Nights)
																		</Text>
																		<Text as="li">
																			Hotel D (3rd, 4th Nights)
																		</Text>
																	</Stack>
																</Stack>
																<Box paddingLeft="2" fontWeight="semibold">
																	Total Price
																	<br />
																	xxx
																</Box>
															</Box>
														</Stack>
													}
												/>
											</Box>
										</Inline>
									</Stack>
									<SwitchInputField
										name="hotel_section_settings.similar_word_in_hotel"
										label="Add Similar Word in Hotels Name"
										help="This can be helpful if you want to add similar word in hotels name - Example -> Taj Hotel / Similar"
									/>
								</Stack>
							</Col>
						</Grid>
						<Divider sm />
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">Template for Subject</Heading>
									<Text color="muted">
										Customize the quote email subject line with placeholder
										variables.
									</Text>
								</Stack>
							</Col>
							<Col>
								<SubjectLineTemplateField variables={subject_line_variables} />
							</Col>
						</Grid>
						<Divider sm />
						<GetFieldValue<typeof settings> name="settings">
							{({ value }) => (
								<Stack gap="2">
									<Grid gap="6">
										<Col sm={12} md={4}>
											<Stack gap="1">
												<Heading fontSize="md">Template for Greetings</Heading>
												<Text color="muted">
													Customize the greetings message according to your
													needs.
												</Text>
											</Stack>
										</Col>
										<Col>
											<GreetingsTemplateField
												fontFamily={value.fontFamily}
												justifiedText={value.justifiedText}
												variables={variables}
											/>
										</Col>
									</Grid>
								</Stack>
							)}
						</GetFieldValue>
						<Divider marginY="0" />
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">
										Settings for Transfer and Activity Section
									</Heading>
									<Text color="muted">
										Customize the section title, variant and other settings.
									</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<Component
										initialState={
											transport_and_activity_section_settings.title
												? true
												: false
										}
									>
										{({ state: editing, setState }) =>
											!editing ? (
												<Stack gap="1">
													<Text fontWeight="semibold">Section Title</Text>
													<Inline
														borderWidth="1"
														rounded="md"
														paddingLeft="4"
														bgColor="subtle"
														gap="4"
														justifyContent="between"
														alignItems="center"
													>
														<Text>Using default title settings</Text>
														<Button
															onClick={() => setState(true)}
															level="tertiary"
														>
															<Icons.Pencil />
														</Button>
													</Inline>
												</Stack>
											) : (
												<TextInputField
													name="transport_and_activity_section_settings.title"
													label="Section Title"
													type="text"
													placeholder="e.g Transportation and Activities"
												/>
											)
										}
									</Component>
									<SwitchInputField
										name="transport_and_activity_section_settings.hide_transport_summary_for_same_cabs"
										label="Hide Transport Summary if Same Cab Type is used"
										help="This can be helpful if you always share full itinerary and only want to include cab details. This WON'T be applicable if you have Activities in Quote"
									/>
									<SwitchInputField
										name="transport_and_activity_section_settings.show_activity_start_time"
										label="Show Activity And Transport Start Time"
										help="Select the checkbox to show the activity start time in the generated PDF"
									/>
									{transport_and_activity_section_settings.variants.length >
									1 ? (
										<SelectField
											label="Variant"
											select={Select}
											name="transport_and_activity_section_settings.variant"
											options={transport_and_activity_section_settings.variants}
										/>
									) : null}
									<MarkdownInputField
										name="transport_and_activity_section_settings.terms"
										label="Specific Terms and Notes"
										placeholder="e.g. **Note**: Vehicles will be point-to-point in the mountain areas."
									/>
								</Stack>
							</Col>
						</Grid>
						{daywise_schedule_section_settings ? (
							<>
								<Divider marginY="0" />
								<Grid gap="6">
									<Col sm={12} md={4}>
										<Stack gap="1">
											<Heading fontSize="md">
												Settings for Day Wise Itinerary Section
											</Heading>
											<Text color="muted">
												Customize the settings of Day-wise Itinerary section
											</Text>
										</Stack>
									</Col>
									<Col>
										<Stack gap="4">
											<TextInputField
												label="Itinerary Section Title"
												name="daywise_schedule_section_settings.title"
												help="Leave blank to remove the section title"
												placeholder="e.g. Day Wise Itinerary"
											/>
											<SwitchInputField
												name="daywise_schedule_section_settings.hide_travel_distance"
												label="Hide Approx. Travel Distance from Itinerary"
												help="This can be helpful if the travel distance includes extra safe running kms which should not be shared."
											/>
											<SwitchInputField
												name="daywise_schedule_section_settings.show_same_day_service_together"
												label="Show Services Together"
												help="Select the checkbox to show the services together."
											/>
										</Stack>
									</Col>
								</Grid>
							</>
						) : null}
						<Divider marginY="0" />
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">
										Show, Hide & Reorder Email Sections
									</Heading>
									<Text color="muted">
										Choose which sections appear in the email and set their
										order. Move sections to the right in the sequence you want
										them to appear. Sections left on the left will be hidden
										from the email.
									</Text>
								</Stack>
							</Col>
							<Col>
								<Box flex="1">
									<Stack gap="4">
										<GetFieldValue<typeof section_ordering_settings>
											name={"section_ordering_settings"}
										>
											{({ value: sections }) => {
												return (
													<>
														{(sections || section_ordering_settings || []).map(
															(section, index) =>
																section.hide === 0 && (
																	<Inline
																		gap="2"
																		alignItems={"center"}
																		key={index}
																	>
																		<Stack
																			size={"6"}
																			alignItems="center"
																			justifyContent="center"
																			borderWidth="1"
																			rounded="full"
																			bgColor="success_emphasis"
																			color="on_emphasis"
																		>
																			<Text fontWeight="semibold">
																				{index + 1}
																			</Text>
																		</Stack>
																		<Box flex="1" minWidth="0">
																			<Box
																				paddingX={"4"}
																				paddingY={"2"}
																				borderWidth="1"
																				rounded="md"
																				maxWidth={"xs"}
																			>
																				<Inline
																					gap="4"
																					alignItems={"center"}
																					justifyContent="between"
																				>
																					<Text>{section.title}</Text>
																				</Inline>
																			</Box>
																		</Box>
																	</Inline>
																)
														)}
														<Box flex="1" paddingLeft={"8"}>
															<GetFieldValue name="hotel_section_settings.show_package_price_below_hotel">
																{({ value }) => (
																	<ReorderSections
																		pricingSectionSettings={isTruthy(value)}
																		sections={sections}
																		onSuccess={(value) => {
																			form.change(
																				"section_ordering_settings",
																				value
																			)
																		}}
																		allowHide
																	>
																		{({ reOrder }) => (
																			<Button
																				status="primary"
																				onClick={reOrder}
																			>
																				<Icons.SwitchHorizontal rotate="90" />
																				Manage Sections
																			</Button>
																		)}
																	</ReorderSections>
																)}
															</GetFieldValue>
														</Box>
													</>
												)
											}}
										</GetFieldValue>
									</Stack>
								</Box>
							</Col>
						</Grid>
						{visa_section_settings ? (
							<>
								<Divider marginY="0" />
								<Grid gap="6">
									<Col sm={12} md={4}>
										<Stack gap="1">
											<Heading fontSize="md">Settings for Visa Section</Heading>
											<Text color="muted">
												Customize the settings of Visa section
											</Text>
										</Stack>
									</Col>
									<Col>
										<TextInputField
											label="Visa Section Title"
											name="visa_section_settings.title"
											placeholder="e.g. Visa"
										/>
									</Col>
								</Grid>
							</>
						) : null}
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">
										Settings for Important Notes Section
									</Heading>
									<Text color="muted">Customize the section title.</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<TextInputField
										name="other_info_section_settings.title"
										label="Section Title"
										type="text"
										placeholder="e.g Important Information"
									/>
								</Stack>
							</Col>
						</Grid>
						<Divider marginY="0" />
						<Grid gap="6">
							<Col sm={12} md={4}>
								<Stack gap="1">
									<Heading fontSize="md">Settings for TnC. Section</Heading>
									<Text color="muted">Customize the section title.</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<TextInputField
										name="terms_section_settings.title"
										label="Section Title"
										type="text"
										placeholder="e.g Terms and Conditions"
									/>
								</Stack>
							</Col>
						</Grid>

						<Grid gap="0">
							<Col sm={12} md={{ offset: 4, span: 8 }}>
								<Stack gap="4" paddingLeft={{ md: "6" }}>
									<SubmissionError />
									<Inline gap="4">
										<Button type="submit" disabled={submitting}>
											{submitting ? "Please wait..." : "Save Template"}
										</Button>
										<Button
											disabled={submitting}
											onClick={() => onCancel()}
											level="tertiary"
										>
											Cancel
										</Button>
									</Inline>
								</Stack>
							</Col>
						</Grid>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function GetBorderWidthFieldValue({
	settingsName,
	children,
}: {
	settingsName: string
	children: (props: {
		borderWidth: [top: number, right: number, bottom: number, left: number]
		hasBorder: boolean
		onChange: (borderWidth: number) => void
	}) => React.ReactNode
}) {
	const { value: top, onChange: changeTop } = useFieldValue<number | "">(
		settingsName + ".borderTopWidth"
	)
	const { value: right, onChange: changeRight } = useFieldValue<number | "">(
		"settings.borderRightWidth"
	)
	const { value: bottom, onChange: changeBottom } = useFieldValue<number | "">(
		"settings.borderBottomWidth"
	)
	const { value: left, onChange: changeLeft } = useFieldValue<number | "">(
		"settings.borderLeftWidth"
	)
	const { borderWidth, hasBorder } = useMemo(() => {
		const border = [
			Number(top || 0),
			Number(right || 0),
			Number(bottom || 0),
			Number(left || 0),
		] as [number, number, number, number]
		return { borderWidth: border, hasBorder: Math.max(...border) > 0 }
	}, [top, right, bottom, left])
	const form = useForm()
	const onChange = useCallback(
		(borderWidth: number) => {
			form.batch(() => {
				changeTop(borderWidth)
				changeRight(borderWidth)
				changeBottom(borderWidth)
				changeLeft(borderWidth)
			})
		},
		[form, changeTop, changeRight, changeBottom, changeLeft]
	)
	return <>{children({ borderWidth: borderWidth, hasBorder, onChange })}</>
}
